import React, { useState, useEffect } from 'react';
import { Modal, ModalBody, ModalHeader, ModalFooter } from 'reactstrap';
import { IFormColumn } from '@/interfaces/form.model';
import PrimaryInput from '@/components/ui/PrimaryInput';
import Button from '@/components/ui/Button';

interface IFieldConfigModalProps {
  isOpen: boolean;
  toggle: () => void;
  onSave: (field: IFormColumn) => void;
  initialData?: IFormColumn;
  sequence: number;
}

const FieldConfigModal: React.FC<IFieldConfigModalProps> = ({
  isOpen,
  toggle,
  onSave,
  initialData,
  sequence,
}) => {
  const defaultField: IFormColumn = {
    id: '',
    label: '',
    columnName: '',
    columnDatatype: 'text',
    required: false,
    sequence: sequence,
    options: [],
    placeholder: '',
    helpText: '',
  };

  const [field, setField] = useState<IFormColumn>(defaultField);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setField(initialData);
      } else {
        setField({ ...defaultField, sequence, id: Math.random().toString() });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialData]);

  const handleChange = <K extends keyof IFormColumn>(
    key: K,
    value: IFormColumn[K],
  ) => {
    setField((prev) => {
      const updated = { ...prev, [key]: value };

      // Auto-generate columnName from label if columnName is untouched/empty
      if (key === 'label' && !prev.columnName && typeof value === 'string') {
        updated.columnName = value.toLowerCase().replace(/[^a-z0-9]/g, '_');
      }
      return updated;
    });
  };

  const handleAddOption = () => {
    setField((prev) => ({
      ...prev,
      options: [...(prev.options || []), { label: '', value: '' }],
    }));
  };

  const handleUpdateOption = (
    index: number,
    key: 'label' | 'value',
    value: string,
  ) => {
    const newOptions = [...(field.options || [])];
    newOptions[index][key] = value;

    // Auto-fill value from label
    if (key === 'label' && !newOptions[index].value) {
      newOptions[index].value = value.toLowerCase().replace(/[^a-z0-9]/g, '_');
    }

    handleChange('options', newOptions);
  };

  const handleRemoveOption = (index: number) => {
    const newOptions = [...(field.options || [])];
    newOptions.splice(index, 1);
    handleChange('options', newOptions);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...field,
      label: field.label.trim(),
      columnName: field.columnName.trim(),
      placeholder: field.placeholder?.trim(),
      helpText: field.helpText?.trim(),
      options: field.options?.map((option) => ({
        label: option.label.trim(),
        value: option.value.trim(),
      })),
    });
    toggle();
  };

  const needsOptions = ['select', 'radio', 'checkbox'].includes(
    field.columnDatatype,
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>
        {initialData ? 'Edit Field' : 'Add New Field'}
      </ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalBody>
          <div className="row g-3">
            <div className="col-md-6">
              <PrimaryInput
                label="Field Label"
                placeholder="e.g. Employee Name"
                value={field.label}
                onChange={(e) => handleChange('label', e.target.value)}
                required
              />
            </div>
            <div className="col-md-6">
              <PrimaryInput
                label="Column Name (Identifier)"
                placeholder="e.g. employee_name"
                value={field.columnName}
                onChange={(e) => handleChange('columnName', e.target.value)}
                required
                // helpText="Unique identifier for API payload"
              />
            </div>

            <div className="col-md-6">
              <label className="form-label">
                Data Type <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={field.columnDatatype}
                onChange={(e) => handleChange('columnDatatype', e.target.value)}
                required
              >
                <option value="text">Text (Single Line)</option>
                <option value="textarea">Text Area (Multi Line)</option>
                <option value="number">Number</option>
                <option value="email">Email</option>
                <option value="date">Date</option>
                <option value="select">Dropdown (Select)</option>
                <option value="radio">Radio Buttons</option>
                <option value="checkbox">Checkbox(es)</option>
                <option value="file">File Upload</option>
              </select>
            </div>

            <div className="col-md-6 d-flex align-items-center mt-4 pt-2">
              <div className="form-check form-switch ms-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="requiredToggle"
                  checked={field.required}
                  onChange={(e) => handleChange('required', e.target.checked)}
                />
                <label className="form-check-label" htmlFor="requiredToggle">
                  Required Field
                </label>
              </div>
            </div>

            <div className="col-md-6">
              <PrimaryInput
                label="Placeholder"
                placeholder="e.g. Enter value here"
                value={field.placeholder || ''}
                onChange={(e) => handleChange('placeholder', e.target.value)}
              />
            </div>

            <div className="col-md-6">
              <PrimaryInput
                label="Help Text"
                placeholder="Instructions for user"
                value={field.helpText || ''}
                onChange={(e) => handleChange('helpText', e.target.value)}
              />
            </div>

            {needsOptions && (
              <div className="col-12 mt-4 bg-light p-3 rounded border">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Options Configuration</h6>
                  <Button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={handleAddOption}
                  >
                    <i className="bi bi-plus-circle me-1"></i> Add Option
                  </Button>
                </div>

                {field?.options && field?.options?.length > 0 ? (
                  <div className="d-flex flex-column gap-2">
                    {field?.options?.map((opt, idx) => (
                      <div key={idx} className="d-flex gap-2">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Label (e.g. Yes)"
                          value={opt.label}
                          onChange={(e) =>
                            handleUpdateOption(idx, 'label', e.target.value)
                          }
                          required
                        />
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Value (e.g. yes)"
                          value={opt.value}
                          onChange={(e) =>
                            handleUpdateOption(idx, 'value', e.target.value)
                          }
                          required
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-danger px-2"
                          onClick={() => handleRemoveOption(idx)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center p-2 text-danger small">
                    At least one option is required for this data type.
                  </div>
                )}
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button type="button" className="btn btn-secondary" onClick={toggle}>
            Cancel
          </Button>
          <Button
            type="submit"
            className="btn btn-primary"
            disabled={
              needsOptions && (!field.options || field.options.length === 0)
            }
          >
            Save Field
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default FieldConfigModal;
